import { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import {
  processDailyBatchData,
  processDailyBatchDataHR,
  processHourlyData,
  processHourlyDataHR,
} from "../util/cdlDataProcessor/cdlDataProcessor";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  removeStats,
  removeStatsHR,
  setStats,
  setStatsHR,
} from "../redux/Dashboard/dashboard.slice";
import { useEffect, useRef, useState } from "react";
import chunk from "lodash/chunk";
import { STATS_FRESHNESS_MINUTES } from "../constants/stats";

export const useDashboardStats = ({
  cdlRepository,
  timeProvider,
  useDailyVitals = false,
}: {
  cdlRepository: ICDLRepository;
  timeProvider: ITimeProvider;
  useDailyVitals?: boolean;
}) => {
  const controllersRef = useRef<AbortController[]>([]);
  const dispatch = useAppDispatch();
  const [dailyStats, setDailyStats] = useState<{
    [key: string]: { avg: number | null; date: string | null };
  }>({});
  const [dailyStatsHR, setDailyStatsHR] = useState<{
    [key: string]: { avg: number | null; date: string | null };
  }>({});

  const { stats, statsHR, statsTimeout } = useAppSelector((state) => ({
    stats: state.dashboard.stats,
    statsHR: state.dashboard.statsHR,
    statsTimeout: state.dashboard.statsTimeout,
  }));

  const statsRef = useRef(stats);
  statsRef.current = stats;
  const statsHRRef = useRef(statsHR);
  statsHRRef.current = statsHR;
  const dailyStatsRef = useRef(dailyStats);
  dailyStatsRef.current = dailyStats;
  const dailyStatsHRRef = useRef(dailyStatsHR);
  dailyStatsHRRef.current = dailyStatsHR;
  const statsTimeoutRef = useRef(statsTimeout);
  statsTimeoutRef.current = statsTimeout;

  const inFlightRRRef = useRef<Set<number>>(new Set());
  const inFlightHRRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    return () => {
      controllersRef.current.forEach((controller) => {
        try {
          controller.abort();
        } catch {
          // Ignore abort errors - controller might already be aborted
        }
      });
      controllersRef.current = [];
    };
  }, []);

  const isStatsFresh = () => {
    if (!statsTimeoutRef.current) return false;
    const cutoff = timeProvider
      .dateLibrary()
      .subtract(STATS_FRESHNESS_MINUTES, "minutes")
      .unix();
    return statsTimeoutRef.current > cutoff;
  };

  const getStatsData = async (patientIds: number[]) => {
    if (useDailyVitals) {
      await getDailyStatsData(patientIds);
      return;
    }

    await getStatsData2(patientIds);
  };

  const getStatsDataHR = async (patientIds: number[]) => {
    if (useDailyVitals) {
      await getDailyStatsDataHR(patientIds);
      return;
    }

    await getStatsDataHR2(patientIds);
  };

  const getLatestNumericEntry = (data: Dictionary<number>) => {
    const numArr = Object.entries(data)
      .filter(([, v]) => {
        return !isNaN(v as number);
      })
      .sort(([dateA], [dateB]) => {
        return dateA.localeCompare(dateB);
      });

    return numArr.length > 0 ? numArr[numArr.length - 1] : null;
  };

  const filterOutAlreadyExisting = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return (
        !Object.hasOwn(statsRef.current, item) ||
        statsRef.current[item].avg === null
      );
    });
  };

  const filterOutAlreadyExistingHR = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return (
        !Object.hasOwn(statsHRRef.current, item) ||
        statsHRRef.current[item].avg === null
      );
    });
  };

  const filterOutAlreadyExistingDaily = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return (
        !Object.hasOwn(dailyStatsRef.current, item) ||
        dailyStatsRef.current[item].avg === null
      );
    });
  };

  const filterOutAlreadyExistingDailyHR = (patientIds: number[]) => {
    return patientIds.filter((item) => {
      return (
        !Object.hasOwn(dailyStatsHRRef.current, item) ||
        dailyStatsHRRef.current[item].avg === null
      );
    });
  };

  const getStatsData2 = async (patientIds: number[]) => {
    const fresh = isStatsFresh();
    let ids = fresh ? filterOutAlreadyExisting(patientIds) : patientIds;

    if (fresh && ids.length === 0) return;

    ids = ids.filter((id) => !inFlightRRRef.current.has(id));
    if (ids.length === 0) return;

    ids.forEach((id) => inFlightRRRef.current.add(id));
    const chunks = chunk(ids, 50);

    try {
      for (const idsArr of chunks) {
        const localController = new AbortController();
        controllersRef.current.push(localController);

        try {
          const response =
            await cdlRepository.getPatientHourlyRRWithoutProcessingV2({
              patient_ids: idsArr,
              date_from: timeProvider
                .getDateTime()
                .subtract(168, "hours")
                .format("YYYY-MM-DDTHH:mm:ssZ"),
              date_to: timeProvider
                .getDateTime()
                .format("YYYY-MM-DDTHH:mm:ssZ"),
              signal: localController.signal,
            });

          if (!response?.data) {
            continue;
          }

          const promisesArray = Object.entries(response.data).map(([k, v]) => ({
            patientId: Number(k),
            data: v,
          }));

          const data = await processHourlyData(promisesArray);

          const newStatsData: {
            [key: string]: { avg: number | null; date: string | null };
          } = {};

          for (let i = 0; i < data.length; i++) {
            const daysRange = data[i].data;
            const returnedPatientId = data[i].patientId;
            newStatsData[returnedPatientId] = { avg: null, date: null };

            const numArr = Object.entries(daysRange).filter(([, v]) => {
              return !isNaN(v as number);
            });
            if (numArr.length > 0) {
              const lastEntry = numArr[numArr.length - 1];
              newStatsData[returnedPatientId] = {
                avg: lastEntry[1] as number,
                date: lastEntry[0],
              };
            }
          }

          dispatch(
            setStats({
              value: newStatsData,
              timeout: timeProvider.dateLibrary().unix(),
            })
          );
        } finally {
          const controllerIndex =
            controllersRef.current.indexOf(localController);
          if (controllerIndex > -1) {
            controllersRef.current.splice(controllerIndex, 1);
          }
        }
      }
    } finally {
      ids.forEach((id) => inFlightRRRef.current.delete(id));
    }
  };

  const getDailyStatsData = async (patientIds: number[]) => {
    let ids = filterOutAlreadyExistingDaily(patientIds);
    ids = ids.filter((id) => !inFlightRRRef.current.has(id));
    if (ids.length === 0) return;

    ids.forEach((id) => inFlightRRRef.current.add(id));
    const chunks = chunk(ids, 50);

    try {
      for (const idsArr of chunks) {
        const localController = new AbortController();
        controllersRef.current.push(localController);

        try {
          const response =
            await cdlRepository.getPatientDailyRRWithoutProcessingV2({
              patient_ids: idsArr,
              date_from: timeProvider
                .getDateTime()
                .subtract(37, "days")
                .format("YYYY-MM-DDT00:00:00Z"),
              date_to: timeProvider
                .getDateTime()
                .format("YYYY-MM-DDT23:59:59Z"),
              signal: localController.signal,
            });

          if (!response?.data) {
            continue;
          }

          const promisesArray = Object.entries(response.data).map(([k, v]) => ({
            patientId: Number(k),
            data: v,
          }));

          const data = await processDailyBatchData(promisesArray);

          const newStatsData: {
            [key: string]: { avg: number | null; date: string | null };
          } = {};

          for (let i = 0; i < data.length; i++) {
            const daysRange = data[i].data;
            const returnedPatientId = data[i].patientId;
            newStatsData[returnedPatientId] = { avg: null, date: null };

            const lastEntry = getLatestNumericEntry(daysRange);
            if (lastEntry) {
              newStatsData[returnedPatientId] = {
                avg: lastEntry[1] as number,
                date: lastEntry[0],
              };
            }
          }

          setDailyStats((prev) => ({
            ...prev,
            ...newStatsData,
          }));
        } finally {
          const controllerIndex =
            controllersRef.current.indexOf(localController);
          if (controllerIndex > -1) {
            controllersRef.current.splice(controllerIndex, 1);
          }
        }
      }
    } finally {
      ids.forEach((id) => inFlightRRRef.current.delete(id));
    }
  };

  const getStatsDataHR2 = async (patientIds: number[]) => {
    const fresh = isStatsFresh();
    let ids = fresh ? filterOutAlreadyExistingHR(patientIds) : patientIds;

    if (fresh && ids.length === 0) return;

    ids = ids.filter((id) => !inFlightHRRef.current.has(id));
    if (ids.length === 0) return;

    ids.forEach((id) => inFlightHRRef.current.add(id));
    const chunks = chunk(ids, 50);

    try {
      for (const idsArr of chunks) {
        const localController = new AbortController();
        controllersRef.current.push(localController);

        try {
          const response =
            await cdlRepository.getPatientHourlyHRWithoutProcessingV2({
              patient_ids: idsArr,
              date_from: timeProvider
                .getDateTime()
                .subtract(168, "hours")
                .format("YYYY-MM-DDTHH:mm:ssZ"),
              date_to: timeProvider
                .getDateTime()
                .format("YYYY-MM-DDTHH:mm:ssZ"),
              signal: localController.signal,
            });

          if (!response?.data) {
            continue;
          }

          const promisesArray = Object.entries(response.data).map(([k, v]) => ({
            patientId: Number(k),
            data: v,
          }));

          const data = await processHourlyDataHR(promisesArray);

          const newStatsData: {
            [key: string]: { avg: number | null; date: string | null };
          } = {};

          for (let i = 0; i < data.length; i++) {
            const daysRange = data[i].data;
            const returnedPatientId = data[i].patientId;
            newStatsData[returnedPatientId] = { avg: null, date: null };

            const numArr = Object.entries(daysRange).filter(([, v]) => {
              return !isNaN(v as number);
            });
            if (numArr.length > 0) {
              const lastEntry = numArr[numArr.length - 1];
              newStatsData[returnedPatientId] = {
                avg: lastEntry[1] as number,
                date: lastEntry[0],
              };
            }
          }

          dispatch(
            setStatsHR({
              value: newStatsData,
              timeout: timeProvider.dateLibrary().unix(),
            })
          );
        } finally {
          const controllerIndex =
            controllersRef.current.indexOf(localController);
          if (controllerIndex > -1) {
            controllersRef.current.splice(controllerIndex, 1);
          }
        }
      }
    } finally {
      ids.forEach((id) => inFlightHRRef.current.delete(id));
    }
  };

  const getDailyStatsDataHR = async (patientIds: number[]) => {
    let ids = filterOutAlreadyExistingDailyHR(patientIds);
    ids = ids.filter((id) => !inFlightHRRef.current.has(id));
    if (ids.length === 0) return;

    ids.forEach((id) => inFlightHRRef.current.add(id));
    const chunks = chunk(ids, 50);

    try {
      for (const idsArr of chunks) {
        const localController = new AbortController();
        controllersRef.current.push(localController);

        try {
          const response =
            await cdlRepository.getPatientDailyHRWithoutProcessingV2({
              patient_ids: idsArr,
              date_from: timeProvider
                .getDateTime()
                .subtract(37, "days")
                .format("YYYY-MM-DDT00:00:00Z"),
              date_to: timeProvider
                .getDateTime()
                .format("YYYY-MM-DDT23:59:59Z"),
              signal: localController.signal,
            });

          if (!response?.data) {
            continue;
          }

          const promisesArray = Object.entries(response.data).map(([k, v]) => ({
            patientId: Number(k),
            data: v,
          }));

          const data = await processDailyBatchDataHR(promisesArray);

          const newStatsData: {
            [key: string]: { avg: number | null; date: string | null };
          } = {};

          for (let i = 0; i < data.length; i++) {
            const daysRange = data[i].data;
            const returnedPatientId = data[i].patientId;
            newStatsData[returnedPatientId] = { avg: null, date: null };

            const lastEntry = getLatestNumericEntry(daysRange);
            if (lastEntry) {
              newStatsData[returnedPatientId] = {
                avg: lastEntry[1] as number,
                date: lastEntry[0],
              };
            }
          }

          setDailyStatsHR((prev) => ({
            ...prev,
            ...newStatsData,
          }));
        } finally {
          const controllerIndex =
            controllersRef.current.indexOf(localController);
          if (controllerIndex > -1) {
            controllersRef.current.splice(controllerIndex, 1);
          }
        }
      }
    } finally {
      ids.forEach((id) => inFlightHRRef.current.delete(id));
    }
  };

  const removeStatsData = (residentIds?: number[]) => {
    dispatch(removeStats(residentIds));
  };

  const removeStatsDataHR = (residentIds?: number[]) => {
    dispatch(removeStatsHR(residentIds));
  };

  return {
    stats: useDailyVitals ? dailyStats : stats,
    getStatsData,
    removeStatsData,
    statsHR: useDailyVitals ? dailyStatsHR : statsHR,
    getStatsDataHR,
    removeStatsDataHR,
  };
};
